'use client';

import { BrandLogo } from '@/components/brand/brand-logo';
import { AccountFooter } from '@/components/chat/account-footer';
import { Thread } from '@/components/ui/thread';
import { ThreadList } from '@/components/ui/thread-list';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { useAuiState } from '@assistant-ui/react';
import { Button, Sheet, SheetContent, SheetTitle, SheetTrigger, cn } from '@iq/ui';
import { MenuIcon, PanelLeftIcon, ShareIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Logo: FC = () => {
  return (
    <div className="flex items-center px-2">
      {/* Theme-aware wordmark: petrol on the light sidebar, the matched-dimension
          dark variant on navy. CSS swap on the .dark class — no invert filter. */}
      <BrandLogo className="w-[80%]" />
    </div>
  );
};

const Sidebar: FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  return (
    <aside
      className={cn(
        'flex h-full flex-col transition-all duration-200',
        collapsed ? 'w-0 overflow-hidden opacity-0' : 'w-65 opacity-100',
      )}
    >
      <div className="flex h-full w-65 shrink-0 flex-col">
        <div className="flex h-14 shrink-0 items-center px-4">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {/* The list refreshes via `runtime.threads.reload()` (wired into
              `thread-list-adapter` from `chat-runtime-provider`), so the
              old `key={version}` remount trick is gone. */}
          <ThreadList />
        </div>
        <div className="shrink-0 border-t border-border/60 p-2">
          <AccountFooter />
        </div>
      </div>
    </aside>
  );
};

const MobileSidebar: FC = () => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="size-9 shrink-0 md:hidden">
          <MenuIcon className="size-4" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="flex w-70 flex-col p-0">
        <SheetTitle className="sr-only">Threads</SheetTitle>
        <div className="flex h-14 shrink-0 items-center px-4">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <ThreadList />
        </div>
        <div className="shrink-0 border-t border-border/60 p-2">
          <AccountFooter />
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Header: FC<{
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}> = ({ sidebarCollapsed, onToggleSidebar }) => {
  const { t } = useTranslation();
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 px-4">
      <MobileSidebar />
      <TooltipIconButton
        variant="ghost"
        size="icon"
        tooltip={sidebarCollapsed ? t('chat.sidebar.show') : t('chat.sidebar.hide')}
        side="bottom"
        onClick={onToggleSidebar}
        className="hidden size-9 md:flex"
      >
        <PanelLeftIcon className="size-4" />
      </TooltipIconButton>
      <TooltipIconButton
        variant="ghost"
        size="icon"
        tooltip={t('chat.actions.share')}
        side="bottom"
        className="ml-auto size-9"
      >
        <ShareIcon className="size-4" />
      </TooltipIconButton>
    </header>
  );
};

export const ChatShell: FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Key the message view by the active thread so switching threads fully
  // remounts it. The runtime (and any in-flight stream) stays mounted across
  // switches; without a remount, message-part components from the previous,
  // longer thread can briefly render against the next thread's shorter message
  // during the swap and throw an out-of-bounds part lookup. Reading the
  // runtime's active remoteId (not the URL) keeps the remount in lockstep with
  // the message swap, so there is no render gap where the stale parts survive.
  const activeThreadKey =
    (useAuiState((s) => s.threadListItem.remoteId) as string | undefined) ?? 'new-thread';

  return (
    <div className="flex h-full w-full bg-muted/30" data-testid="chat-route">
      <div className="hidden md:block">
        <Sidebar collapsed={sidebarCollapsed} />
      </div>
      <div
        className={cn(
          'flex flex-1 flex-col overflow-hidden p-2 transition-[padding] duration-200',
          !sidebarCollapsed && 'md:pl-0',
        )}
      >
        <div
          className="flex flex-1 flex-col overflow-hidden rounded-lg bg-background"
          data-testid="chat-surface"
        >
          <Header
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <main className="flex-1 overflow-hidden">
            <Thread key={activeThreadKey} />
          </main>
        </div>
      </div>
    </div>
  );
};
